import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {
  Container,
  Col,
  Row,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Card,
  CardHeader,
  CardBody,
  Badge,
  ButtonGroup,
  Button,
} from "reactstrap";
import classnames from "classnames";
import {Link, useNavigate} from "react-router-dom";
// import "./LeadersAndLaggards.css";

import {TopStocksProps} from "../containers/TopStocks";
import {getTickerType, TickerType} from "../utils/typeValidation";
import {buildDashboardUrl} from "../utils/links";
import {SignalCell, PatternCell, MomentumCell} from "./SignalCells";

const FREE_TIER_LIMIT = 5;

interface StocksCellsProps {
  stocks: TopStocksRow[];
  type: "leaders" | "upcoming" | "laggards" | "weakening";
}

function StockRow(props: StocksCellsProps) {
  const {stocks, type} = props;
  const navigate = useNavigate();

  // Define score styling based on stock type
  const getScoreClass = (type: string) => {
    return type === "leaders" || type === "upcoming"
      ? "score-positive"
      : "score-negative";
  };

  return (
    <>
      {stocks.map((row, i) => (
        <tr
          key={`${type}-row-${i}`}
          className="top-stocks-row"
          onClick={() => navigate(buildDashboardUrl({ticker: row.ticker, window: 20}))}
        >
          <td className="rank-cell">
            <Badge>{i + 1}</Badge>
          </td>
          <td className="ticker-cell">
            <Link
              to={buildDashboardUrl({ticker: row.ticker, window: 20})}
              rel="nofollow"
              className="stock-link"
              onClick={(e) => e.stopPropagation()}
            >
              {row.ticker}
            </Link>
            {row.ticker_name && (
              <div className="small text-muted">{row.ticker_name}</div>
            )}
          </td>
          <td className={`score-cell ${getScoreClass(type)}`}>{row.rank.toFixed(2)}</td>
          <td className="signal-cell">
            <SignalCell s={row.signals} />
          </td>
          <td className="pattern-cell">
            <PatternCell s={row.signals} />
          </td>
          <td className="momentum-cell">
            <MomentumCell s={row.signals} />
          </td>
          <td className="description-cell">
            <span className="small text-muted">{row.description}</span>
          </td>
        </tr>
      ))}
    </>
  );
}

const LeadersAndLaggards: React.FC<TopStocksProps> = (props) => {
  const {getTopStocks, topStocks} = props;
  const authState = useSelector((state: RootReducerState) => state.authentication);
  const isPro = authState.user?.groups?.includes('pro') ?? false;
  const isLimited = !isPro; // both anonymous and free tier see top 5 only

  useEffect(() => {
    getTopStocks();
  }, [getTopStocks]);

  const [activeTab, setActiveTab] = useState("1");
  const [tickerTypeFilter, setTickerTypeFilter] = useState<TickerType | 'all'>('all');

  const toggle = (tab: string) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const filterByType = (stocks: TopStocksRow[]) => {
    if (tickerTypeFilter === 'all') return stocks;
    return stocks.filter((row) => getTickerType(row.ticker) === tickerTypeFilter);
  };

  const applyLimit = (stocks: TopStocksRow[]) =>
    isLimited ? stocks.slice(0, FREE_TIER_LIMIT) : stocks;

  const strongStocks = applyLimit(filterByType(topStocks?.strongStocks || []));
  const improvingStocks = applyLimit(filterByType(topStocks?.improvingStocks || []));
  const weakStocks = applyLimit(filterByType(topStocks?.weakStocks || []));
  const weakeningStocks = applyLimit(filterByType(topStocks?.weakeningStocks || []));

  return (
    <Container className="leaders-laggards-container">
      <div className="page-header">
        <h1 className="page-title">Leaders & Laggards</h1>
        <div className="header-line"></div>
      </div>

      <p className="text-muted mb-4">
        Rankings based purely on relative price strength across multiple timeframes — not
        related to pattern matching or chart pattern detection. This is a momentum-based
        view of which stocks are outperforming or underperforming the broader market.
      </p>

      {activeTab === "1" && (
        <Row className="description-row">
          <Col>
            <div className="description-card">
              <h5 className="description-title positive-title">
                <i className="indicator-arrow up-arrow"></i>
                Leaders & Up and Coming
              </h5>
              <p>
                The DejaChart <b>Leaders</b> are the strongest stocks that we track. These
                stocks have shown great momentum over time and consistently outperform
                their peers.
              </p>
              <p>
                The DejaChart <b>Up and Coming</b> stocks are making significant
                advancement up the Leaders list. These are improving stocks, weak stocks
                making a comeback, or leaders solidifying their position.
              </p>
            </div>
          </Col>
        </Row>
      )}
      {activeTab === "2" && (
        <Row className="description-row">
          <Col>
            <div className="description-card">
              <h5 className="description-title negative-title">
                <i className="indicator-arrow down-arrow"></i>
                Laggards & Weakening
              </h5>
              <p>
                The DejaChart <b>Laggards</b> are the weakest stocks that we track. These
                stocks have shown significant downward momentum over time and consistently
                underperform.
              </p>
              <p>
                The DejaChart <b>Weakening</b> stocks are making significant advancement
                up the Laggards list. These are declining stocks, strong stocks showing
                weakness, or laggards continuing their downtrend.
              </p>
            </div>
          </Col>
        </Row>
      )}

      <div className="mb-3">
        <ButtonGroup>
          <Button color={tickerTypeFilter === 'all' ? 'primary' : 'secondary'} outline={tickerTypeFilter !== 'all'} onClick={() => setTickerTypeFilter('all')}>All</Button>
          <Button color={tickerTypeFilter === 'stock' ? 'primary' : 'secondary'} outline={tickerTypeFilter !== 'stock'} onClick={() => setTickerTypeFilter('stock')}>Stocks/ETFs</Button>
          <Button color={tickerTypeFilter === 'index' ? 'primary' : 'secondary'} outline={tickerTypeFilter !== 'index'} onClick={() => setTickerTypeFilter('index')}>Indexes</Button>
          <Button color={tickerTypeFilter === 'crypto' ? 'primary' : 'secondary'} outline={tickerTypeFilter !== 'crypto'} onClick={() => setTickerTypeFilter('crypto')}>Crypto & Forex</Button>
          <Button color={tickerTypeFilter === 'futures' ? 'primary' : 'secondary'} outline={tickerTypeFilter !== 'futures'} onClick={() => setTickerTypeFilter('futures')}>Futures</Button>
        </ButtonGroup>
      </div>

      <div className="tabs-container">
        <Nav tabs className="custom-tabs">
          <NavItem>
            <NavLink
              className={classnames({active: activeTab === "1"})}
              onClick={() => toggle("1")}
            >
              <div className="tab-label positive-tab">
                <i className="tab-icon leaders-icon"></i>
                Leaders
              </div>
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({active: activeTab === "2"})}
              onClick={() => toggle("2")}
            >
              <div className="tab-label negative-tab">
                <i className="tab-icon laggards-icon"></i>
                Laggards
              </div>
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab} className="custom-tab-content">
          <TabPane tabId="1">
            <Row className="table-row">
              <Col md="6">
                <Card className="stock-card leaders-card">
                  <CardHeader className="stock-card-header">
                    <h4 className="card-title">Leaders</h4>
                    <span className="card-subtitle">Top performing stocks</span>
                  </CardHeader>
                  <CardBody className="stock-card-body">
                    <Table className="stock-table" responsive borderless>
                      <thead>
                        <tr>
                          <th className="rank-header">Rank</th>
                          <th className="ticker-header">Symbol</th>
                          <th className="score-header">Strength Score</th>
                          <th className="signal-header">Signal</th>
                          <th className="pattern-header">Pattern</th>
                          <th className="momentum-header">Momentum</th>
                          <th className="description-header">Why</th>
                        </tr>
                      </thead>
                      <tbody>
                        <StockRow stocks={strongStocks} type="leaders" />
                      </tbody>
                    </Table>
                    {isLimited && (
                      <div className="text-center py-2">
                        <small className="text-muted">Showing top {FREE_TIER_LIMIT}. </small>
                        <Link to="/pricing" className="small">Upgrade to Pro</Link>
                        <small className="text-muted"> to see the full list.</small>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
              <Col md="6">
                <Card className="stock-card upcoming-card">
                  <CardHeader className="stock-card-header">
                    <h4 className="card-title">Up and Coming</h4>
                    <span className="card-subtitle">Improving momentum</span>
                  </CardHeader>
                  <CardBody className="stock-card-body">
                    <Table className="stock-table" responsive borderless>
                      <thead>
                        <tr>
                          <th className="rank-header">Rank</th>
                          <th className="ticker-header">Symbol</th>
                          <th className="score-header">Improvement Score</th>
                          <th className="signal-header">Signal</th>
                          <th className="pattern-header">Pattern</th>
                          <th className="momentum-header">Momentum</th>
                          <th className="description-header">Why</th>
                        </tr>
                      </thead>
                      <tbody>
                        <StockRow stocks={improvingStocks} type="upcoming" />
                      </tbody>
                    </Table>
                    {isLimited && (
                      <div className="text-center py-2">
                        <small className="text-muted">Showing top {FREE_TIER_LIMIT}. </small>
                        <Link to="/pricing" className="small">Upgrade to Pro</Link>
                        <small className="text-muted"> to see the full list.</small>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </TabPane>

          <TabPane tabId="2">
            <Row className="table-row">
              <Col md="6">
                <Card className="stock-card laggards-card">
                  <CardHeader className="stock-card-header">
                    <h4 className="card-title">Laggards</h4>
                    <span className="card-subtitle">Underperforming stocks</span>
                  </CardHeader>
                  <CardBody className="stock-card-body">
                    <Table className="stock-table" responsive borderless>
                      <thead>
                        <tr>
                          <th className="rank-header">Rank</th>
                          <th className="ticker-header">Symbol</th>
                          <th className="score-header">Weakness Score</th>
                          <th className="signal-header">Signal</th>
                          <th className="pattern-header">Pattern</th>
                          <th className="momentum-header">Momentum</th>
                          <th className="description-header">Why</th>
                        </tr>
                      </thead>
                      <tbody>
                        <StockRow stocks={weakStocks} type="laggards" />
                      </tbody>
                    </Table>
                    {isLimited && (
                      <div className="text-center py-2">
                        <small className="text-muted">Showing top {FREE_TIER_LIMIT}. </small>
                        <Link to="/pricing" className="small">Upgrade to Pro</Link>
                        <small className="text-muted"> to see the full list.</small>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
              <Col md="6">
                <Card className="stock-card weakening-card">
                  <CardHeader className="stock-card-header">
                    <h4 className="card-title">Weakening</h4>
                    <span className="card-subtitle">Declining momentum</span>
                  </CardHeader>
                  <CardBody className="stock-card-body">
                    <Table className="stock-table" responsive borderless>
                      <thead>
                        <tr>
                          <th className="rank-header">Rank</th>
                          <th className="ticker-header">Symbol</th>
                          <th className="score-header">Downside Score</th>
                          <th className="signal-header">Signal</th>
                          <th className="pattern-header">Pattern</th>
                          <th className="momentum-header">Momentum</th>
                          <th className="description-header">Why</th>
                        </tr>
                      </thead>
                      <tbody>
                        <StockRow stocks={weakeningStocks} type="weakening" />
                      </tbody>
                    </Table>
                    {isLimited && (
                      <div className="text-center py-2">
                        <small className="text-muted">Showing top {FREE_TIER_LIMIT}. </small>
                        <Link to="/pricing" className="small">Upgrade to Pro</Link>
                        <small className="text-muted"> to see the full list.</small>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </TabPane>
        </TabContent>
      </div>

      <div className="additional-info">
        <div className="info-box">
          <div className="info-icon"></div>
          <div className="info-content">
            <h5>How are scores calculated?</h5>
            <p>
              Our proprietary scoring algorithm analyzes multiple technical indicators,
              historical price action, and relative performance metrics. The algorithm
              assigns weights to each factor based on market conditions to generate the
              final score.
            </p>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default LeadersAndLaggards;
